export default {
  name: 'dashify:SingleValueArray',
  title: 'Single Value Array',
  owner: 'System',
  solution: '',
  tags: ['widget', 'numeric', 'single value', 'array'],
  createdAt: 'May 30, 2023, 10:14 AM',
  modifiedAt: 'July 21, 2023, 2:34 PM',
  spec: {
    props: {
      title: '',
      data: [],
      unit: '',
      href: '',
      valueTextSize: 'text-2xl',

      shortFormat: false,
      showDelta: true,
      compareAsPercent: false,
      comparisonStart: 'RECENT',
      showDescription: false,

      alignment: 'LEFT',
      colorZones: [],
      backgroundColorClassName: '',
      titleColorClassName: '',
      valueColorClassName: '',
      drillDownHoverColorClassName: 'hover:bg-neutral-200/20',
      descriptionColorClassName: '',
      unitColorClassName: '',
    },

    title: '/${ props.title }',
    data: '${ props.data }',
    dataCount: '/${ $count(data) }',
    unit: '/${ props.unit }',
    shortFormat: '/${ dataCount > 0 ? props.shortFormat : false }',
    showDescription: '/${ props.showDescription }',
    showDelta: '/${ props.showDelta }',
    comparisonStart: '${ props.comparisonStart }',
    compareAsPercent: '/${ props.compareAsPercent }',
    href: '/${ props.href }',

    // Delta
    comparisonMetric: '${ comparisonStart = "OVERALL" ? data[0] : data[-2] }',
    delta: '/${ $round(data[-1] - comparisonMetric, 2) }',
    deltaPercent: '/${ $round((data[-1] - comparisonMetric)/comparisonMetric * 100, 2) }',
    noTrend: '/${ dataCount < 2 or delta = 0 }',
    trendArrow: '${ noTrend ? "" : delta > 0 ? "↑" : "↓" }',
    formattedDelta:
      "/${ compareAsPercent and unit != '%' ? trendArrow & ' ' & $toNumberShort($abs(deltaPercent)) & '%' : trendArrow & ' ' & $toNumberShort($abs(delta)) }",

    // TimeRange
    globalsSince: '/${ globals.variables.since }',
    globalsUntil: '/${ globals.variables.until }',
    isCustomTimeRange: '/${ $not($contains(globalsSince, "now")) }',
    // parse relative time range string
    relativeTimeSince: "/${ $substringAfter(globalsSince, ' ') }",
    relativeTimeSinceParsedNum: '/${ $substring(relativeTimeSince, 1, $length(relativeTimeSince) - 2) ~> $number() }',
    relativeTimeUnit: '/${ $substring(relativeTimeSince, $length(relativeTimeSince) - 1, 1) }',
    msMultiplier: {
      w: 1000 * 60 * 60 * 24 * 7,
      d: 1000 * 60 * 60 * 24,
      h: 1000 * 60 * 60,
      m: 1000 * 60,
    },
    relativeTimeSinceMS: '/${ $millis() - relativeTimeSinceParsedNum * $lookup(msMultiplier, relativeTimeUnit) }',
    // build formatted time range string
    formattedSince:
      '/${ isCustomTimeRange ? $formatDateTime(globalsSince) : $fromMillis(relativeTimeSinceMS) ~> $formatDateTime() }',
    formattedUntil: '/${ isCustomTimeRange ? $formatDateTime(globalsUnitl) : $formatDateTime($now()) }',
    TIMERANGE: "/${ formattedSince & ' - ' & formattedUntil }",

    // Styling
    alignmentClassNames: {
      LEFT: 'items-start',
      CENTER: 'items-center',
      RIGHT: 'items-end',
    },

    alignment: '/${ $uppercase(props.alignment) }',
    alignmentClassName: '/${ $lookup(alignmentClassNames, alignment) }',

    defaultColorZones: [
      { value: -0.001, color: 'text-green-400' },
      { value: 0, color: 'text-gray-400' },
      { color: 'text-red-400' },
    ],
    colorZones: '/${ $count(props.colorZones) = 0 ? defaultColorZones : props.colorZones }',
    // sort function callback makes sure correct color zone is used and default color zone - no value - get placed properly
    zone: '/${ $filter(colorZones, function($v){ $not($exists($v.value)) or deltaPercent <= $v.value}) ~> $sort(function($l,$r){ $not($exists($l.value)) or $l.value > $r.value}) }',
    deltaColorClassName: '/${ zone[0].color }',
    unitColorClassName: '/${ props.unitColorClassName }',

    containerClassName:
      "/${ 'text-sm px-3 mx-2 flex flex-col justify-center ' & props.backgroundColorClassName & ' ' & alignmentClassName }",
    titleClassName: "/${ 'opacity-80 mb-2 ' & props.titleColorClassName }",
    valueClassName: "/${ 'flex items-baseline whitespace-nowrap mb-2 ' & props.valueColorClassName }",
    deltaClassName: "/${ deltaColorClassName & ' justify-self-end ml-3' }",
    drillDownClassName:
      "/${ 'hover:rounded-md hover:text-white/90 transition-colors ease-out duratin-1000 ' & props.drillDownHoverColorClassName }",
    descriptionClassName: "/${ 'flex items-end w-full ' & props.descriptionColorClassName }",
    unitClassName: "/${ 'text-md mx-1 ' & unitColorClassName }",

    view: [
      {
        instanceOf: 'div',
        className: '/${ containerClassName }',
        children: [
          // Title
          {
            instanceOf: 'p',
            className: '/${ titleClassName }',
            children: '/${ title }',
          },
          // Value
          {
            instanceOf: 'div',
            className: '/${ valueClassName }',
            children: [
              {
                instanceOf: 'a',
                href: '/${ href }',
                className: '/${ href ? drillDownClassName : "pointer-events-none" }',
                children: [
                  {
                    instanceOf: 'div',
                    className: "/${'m-0 ' & (props.valueTextSize? props.valueTextSize : 'text-2xl')}",
                    children: [
                      [
                        'dashify:If',
                        { condition: '/${ dataCount = 0 }' },
                        [
                          ['dashify:Then', {}, [['p', {}, '—']]],
                          [
                            'dashify:Else',
                            {},
                            [['p', {}, '/${ shortFormat ? $toNumberShort(data[-1]) : $string(data[-1]) }']],
                          ],
                        ],
                      ],
                    ],
                  },
                ],
              },
              {
                instanceOf: 'p',
                className: '/${ unit ? unitClassName : "hidden" }',
                children: '/${ unit }',
              },
              [
                'dashify:If',
                { condition: '/${ showDelta and dataCount > 1 }' },
                [
                  {
                    instanceOf: 'p',
                    className: '/${ deltaClassName }',
                    children: '/${ formattedDelta }',
                  },
                ],
              ],
            ],
          },
          // Description
          [
            'dashify:If',
            { condition: '/${ showDescription }' },
            [
              [
                'dashify:Then',
                {},
                [
                  {
                    instanceOf: 'div',
                    className: '/${ descriptionClassName }',
                    children: [
                      {
                        instanceOf: 'div',
                        className: 'basis-3/4',
                        children: [
                          {
                            instanceOf: 'div',
                            className: '',
                            children:
                              '/${ comparisonMetric = "" ? "previous value" : shortFormat ? $toNumberShort(comparisonMetric) : comparisonMetric }',
                          },
                          {
                            instanceOf: 'div',
                            className: 'opacity-60',
                            children: [['p', {}, '/${ TIMERANGE }']],
                          },
                        ],
                      },
                      {
                        instanceOf: 'div',
                        className: 'w-[1px] h-full mx-2 bg-neutral-100 opacity-30',
                      },

                      {
                        instanceOf: 'div',
                        className: 'flex flex-col items-center',
                        children: [
                          {
                            instanceOf: 'div',
                            className: '/${ deltaColorClassName }',
                            children: '/${ formattedDelta }',
                          },
                          {
                            instanceOf: 'div',
                            className: 'opacity-60',
                            children: 'Change',
                          },
                        ],
                      },
                    ],
                  },
                ],
              ],
            ],
          ],
        ],
      },
    ],
  },
};
